// pages/reports/CourseCompletionMatrix.js
import React, { useState } from 'react';
import { Button, Spinner, Table } from 'react-bootstrap';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import useReportData from '../../hooks/useReportData';

// Departments arrive pre-sorted by company then department name. Grouping
// consecutive same-company rows gives the column spans for the two-row
// header — this only produces more than one group for master admin, whose
// department list spans multiple companies; company/department admin are
// always scoped to a single company, so they naturally fall back to a
// plain one-row header below.
function groupByCompany(departments) {
  const groups = [];
  for (const dept of departments) {
    const last = groups[groups.length - 1];
    if (last && last.companyId === dept.company_id) {
      last.departments.push(dept);
    } else {
      groups.push({ companyId: dept.company_id, companyName: dept.company_name, departments: [dept] });
    }
  }
  return groups;
}

function CourseCompletionMatrix() {
  const [filters, setFilters] = useState({ period: 'all_time' });
  const { data, loading, error, exportUrl } = useReportData('reports/get_course_completion_matrix.php', filters);

  const departments = data?.departments ?? [];
  const courses = data?.courses ?? [];
  const matrix = data?.matrix ?? {};
  const companyGroups = groupByCompany(departments);
  const showCompanyRow = companyGroups.length > 1;

  return (
    <div className="p-2">
      <div className="d-flex justify-content-between align-items-center no-print">
        <h2>Course Completion Matrix</h2>
        <div>
          <Button variant="outline-secondary" className="me-2" onClick={() => window.print()} disabled={!data}>
            Print
          </Button>
          {exportUrl && (
            <Button as="a" href={exportUrl} variant="outline-primary">
              Export to CSV
            </Button>
          )}
        </div>
      </div>

      <ReportFilterBar value={filters} onChange={setFilters} showDepartmentFilter={false} />

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <Spinner animation="border" size="sm" />}

      {data && !loading && (
        <>
          <h5 className="text-muted">{data.start && data.end ? `${data.start} to ${data.end}` : 'All Time'}</h5>

          {departments.length === 0 ? (
            <div className="alert alert-info">No departments in scope.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead>
                  {showCompanyRow && (
                    <tr>
                      <th rowSpan={2}>Course</th>
                      {companyGroups.map(group => (
                        <th key={group.companyId} colSpan={group.departments.length} className="text-center">
                          {group.companyName}
                        </th>
                      ))}
                    </tr>
                  )}
                  <tr>
                    {!showCompanyRow && <th>Course</th>}
                    {departments.map(dept => (
                      <th key={dept.id}>{dept.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="table-secondary fw-bold">
                    <td>Total Employees</td>
                    {departments.map(dept => (
                      <td key={dept.id} className="text-center">{dept.employeeCount ?? 0}</td>
                    ))}
                  </tr>
                  {courses.length > 0 ? courses.map(course => (
                    <tr key={course.id}>
                      <td>{course.code} — {course.title}</td>
                      {departments.map(dept => (
                        <td key={dept.id} className="text-center">{matrix[course.id]?.[dept.id] ?? 0}</td>
                      ))}
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={departments.length + 1} className="text-center">No courses found</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default CourseCompletionMatrix;
